<template>
  <fullscreen
    :fullscreen.sync="isFullscreen"
    fullscreen-class="presentation--full-screen"
    :teleport="false"
    @change="handleFullscreenChange"
  >
    <template v-if="!isCollaborativeDocument && fileType !=='file'">
      <b-overlay
        :show="isLoading"
        class="pdf-reader-container"
        rounded="sm"
        spinner-type="grow"
      >
        <pdf-viewer
          ref="pdf-reader"
          class="pdf-reader"
          :src="media.url"
          :show-controls="false"
          :allow-fullscreen="true"
          :initial-page="currentPage"
          :initial-scale="scale"
          @loaded="handleDocumentLoaded"
          @page-change="handlePageChange"
          @scale-change="handleScaleChange"
        />
      </b-overlay>

      <!-- PDF Controls -->
      <div class="barra py-1">
        <div class="d-flex justify-content-center align-items-center flex-wrap">
          <!-- Navigation -->
          <b-button-group size="sm" class="mx-1">
            <b-button
              :disabled="currentPage <= 1"
              @click="goToPreviousPage"
              variant="outline-primary"
            >
              <font-awesome-icon icon="chevron-left" fixed-width />
            </b-button>
            <b-button variant="outline-primary" class="px-3" disabled>
              {{ currentPage }} / {{ numPages }}
            </b-button>
            <b-button
              :disabled="currentPage >= numPages"
              @click="goToNextPage"
              variant="outline-primary"
            >
              <font-awesome-icon icon="chevron-right" fixed-width />
            </b-button>
          </b-button-group>

          <!-- Zoom -->
          <b-button-group size="sm" class="mx-1">
            <b-button @click="zoomOut" variant="outline-primary">
              <font-awesome-icon icon="search-minus" fixed-width />
            </b-button>
            <b-dropdown variant="outline-primary" size="sm" :text="zoomPercentage">
              <b-dropdown-item
                v-for="level in zoomLevels"
                :key="level"
                @click="setZoom(level)"
              >
                {{ Math.round(level * 100) }}%
              </b-dropdown-item>
            </b-dropdown>
            <b-button @click="zoomIn" variant="outline-primary">
              <font-awesome-icon icon="search-plus" fixed-width />
            </b-button>
          </b-button-group>

          <!-- Tools -->
          <b-button-group size="sm" class="mx-1">
            <b-button @click="rotate" variant="outline-primary">
              <font-awesome-icon icon="redo" fixed-width />
            </b-button>
            <b-button @click="toggleFullscreen" variant="outline-primary">
              <font-awesome-icon :icon="isFullscreen ? 'compress' : 'expand'" fixed-width />
            </b-button>
          </b-button-group>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="open">
        <safe-img
          :src="previewPlaceholder"
          alt="preview photo"
          class="preview-image w-100"
          style="height:110%"
        />
        <div v-if="fileType==='file'" class="h-100 w-100 open--container">
          <b-button
            variant="outline-light"
            class="open--file"
            :href="media.url"
            target="_blank"
          >
            {{ $t('media.download-file') }}
          </b-button>
        </div>
        <div v-if="isCollaborativeDocument" class="h-100 w-100 open--container">
          <b-button variant="outline-light" class="open--file" :to="collaborativeLocation">
            {{ $t('media.edit-collaboratively-action') }}
          </b-button>
        </div>
        <!--         <b-embed :src="media.url" allowfullscreen class="video-border" />
 -->
      </div>
    </template>
  </fullscreen>
</template>

<script>
import {
  BOverlay, BButton, BEmbed, BFormInput, BButtonGroup, BDropdown, BDropdownItem,
} from 'bootstrap-vue';
import { component as fullscreen } from 'vue-fullscreen';
import { getImageResource } from '@/@core/utils/image-utils';
import DocPlaceholder from '@/assets/images/placeholders/light/banner_doc.png';
import PresentationPlaceholder from '@/assets/images/placeholders/light/banner_presentacion.png';
import SpreadsheetPlaceholder from '@/assets/images/placeholders/light/banner_excel.png';
import FilePlaceholder from '@/assets/images/placeholders/light/banner_file.png';
import SafeImg from '@core/components/safe-img/SafeImg.vue';
import PdfViewer from '@/components/PdfViewer.vue';

export default {
  name: 'MediaPresentationRenderer',
  components: {
    BOverlay,
    BButton,
    BButtonGroup,
    BDropdown,
    BDropdownItem,
    fullscreen,
    PdfViewer,
    SafeImg,
    BEmbed,
    BFormInput,
  },
  props: {
    media: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      isLoadingPage: false,
      numPages: 1,
      currentPage: 1,
      isFullscreen: false,
      scale: 0.75,
      zoomLevels: [0.5, 0.75, 1, 1.25, 1.5, 2],
    };
  },
  computed: {
    zoomPercentage() {
      return Math.round(this.scale * 100) + '%';
    },
    canGoToNextPage() {
      // sroll to top of the page
      this.scrollToTop();
      return !this.isLoading && !this.isLoadingPage && this.numPages && this.currentPage < this.numPages;
    },
    canGoToPreviousPage() {
      // sroll to top of the page
      this.scrollToTop();
      return !this.isLoading && !this.isLoadingPage && this.currentPage > 1;
    },
    previewPlaceholder() {
      switch (this.fileType) {
        case 'xlsx':
          return SpreadsheetPlaceholder;
        case 'pptx':
          return PresentationPlaceholder;
        case 'docx':
          return DocPlaceholder;
        default:
          return FilePlaceholder;
      }
    },
    isCollaborativeDocument() {
      if (this.media.url.includes('.docx') || this.media.url.includes('.doc') || this.media.url.includes('.pptx') || this.media.url.includes('.xlsx') || this.media.isCollaborative) {
        return true;
      }
      return false;
    },
    collaborativeLocation() {
      return {
        name: 'collaborative-documents',
        params: { id: this.media.key, media: this.media, communityId: this.$route.params.communityId },
      };
    },
    fileType() {
      if (this.media.url.includes('.docx')) {
        return 'docx';
      }
      if (this.media.url.includes('.pptx')) {
        return 'pptx';
      }
      if (this.media.url.includes('.xlsx')) {
        return 'xlsx';
      } if (this.media.url.includes('.pdf')) {
        return 'xlsx';
      } if (this.media.mediaType === 'file') {
        return 'file';
      }
      return 'xlsx';
    },
  },
  created() {
    window.addEventListener('keyup', this.handleKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeyUp);
  },
  methods: {
    handleKeyUp({ key }) {
      switch (key) {
        case 'ArrowRight':
          if (this.canGoToNextPage) {
            this.goToNextPage();
          }
          break;
        case 'ArrowLeft':
          if (this.canGoToPreviousPage) {
            this.goToPreviousPage();
          }
          break;
        default:
          break;
      }
    },
    handleDocumentLoaded(event) {
      this.isLoading = false;
      this.numPages = event.pagesCount;
    },
    handlePageChange(pageNumber) {
      this.currentPage = pageNumber;
    },
    handleScaleChange(scale) {
      this.scale = scale;
    },
    goToNextPage() {
      const reader = this.$refs['pdf-reader'];
      if (reader) reader.nextPage();
    },
    goToPreviousPage() {
      const reader = this.$refs['pdf-reader'];
      if (reader) reader.previousPage();
    },
    zoomIn() {
      const reader = this.$refs['pdf-reader'];
      if (!reader) return;
      
      const nextLevel = this.zoomLevels.find(level => level > this.scale);
      if (nextLevel) {
        reader.setZoom(nextLevel);
      } else {
        reader.setZoom(this.zoomLevels[this.zoomLevels.length - 1]);
      }
    },
    zoomOut() {
      const reader = this.$refs['pdf-reader'];
      if (!reader) return;
      
      const prevLevel = [...this.zoomLevels].reverse().find(level => level < this.scale);
      if (prevLevel) {
        reader.setZoom(prevLevel);
      } else {
        reader.setZoom(this.zoomLevels[0]);
      }
    },
    setZoom(scale) {
      const reader = this.$refs['pdf-reader'];
      if (reader) {
        reader.setZoom(scale);
      }
    },
    rotate() {
      const reader = this.$refs['pdf-reader'];
      if (reader) reader.rotate();
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      
      this.$nextTick(() => {
        if (this.isFullscreen) {
          this.setZoom(1);
        } else {
          this.setZoom(0.75);
        }
      });
    },
    async handleFullscreenChange(isFullscreen) {
      if (isFullscreen) {
        await this.$nextTick();
        const { offsetWidth, offsetHeight, scrollHeight } = this.$refs['pdf-reader'].$el;

        // set new width that respects aspect ratio.
        this.$refs['pdf-reader'].$el.style.width = `${(offsetWidth / scrollHeight) * offsetHeight}px`;
      } else {
        this.$refs['pdf-reader'].$el.style.width = null;
      }
    },
    getImageResource,
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth' // Optional: for smooth scrolling
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-include';
.presentation--full-screen {
  display: flex;
  flex-direction: column;
  .pdf-reader-container {
    overflow-y: auto;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  .pdf-reader {
    flex-grow: 1;
    margin: auto;
  }
}


.form-control {
  width: 80px;
  max-width: 80px;
}

.barra {
  position: relative;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin: 0.5rem auto 0;
  padding: 0.375rem 0.75rem;
}
.open {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  &--container {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    opacity: 0;
    align-items: center;
    justify-content: center;
    background-color: rgba(32,32,32,0.6);
    border-radius:12px;
  }
  &--container:hover {
    opacity: 100;
    transition: opacity 0.5s;
  }
}
.preview-image {
  @import '~@core/scss/base/bootstrap-include';
  max-height: 400px;
  object-fit: cover;
  width: 80vw;
  @include media-breakpoint-up(md) {
    width: auto;
  }
}
</style>
